/**
 * ContentPanel.tsx — Mittlere Spalte des App-Layouts
 *
 * Zeigt je nach aktiver NavRail-Section:
 * - Eine Liste (Reports, Docs, Audits, Notes, Scenes, Punktwolke)
 * - Eine Detail-/Editor-Ansicht (wenn ein Item ausgewählt)
 * - Platzhalter (Agent, Emmissionssenken, User)
 *
 * Hat eigenen Header mit Back-Navigation, Filter und Create-Button.
 */

import React from 'react'
import type { NavSection } from './Sidebar'

interface ContentPanelProps {
  section: NavSection
  children: React.ReactNode
  /** Title for the panel header */
  title: string
  /** Show back button + call this handler */
  onBack?: (() => void) | null
  /** Forward navigation */
  onForward?: (() => void) | null
  /** Filter button handler */
  onFilter?: (() => void) | null
  /** Create (+) button handler */
  onCreate?: (() => void) | null
  /** Extra action in header (e.g. "edit" button) */
  headerAction?: React.ReactNode
  /** Additional CSS class (e.g. for overlay mode) */
  className?: string
}

export function ContentPanel({
  section,
  children,
  title,
  onBack,
  onForward,
  onFilter,
  onCreate,
  headerAction,
  className,
}: ContentPanelProps) {
  return (
    <div className={`content-panel${className ? ` ${className}` : ''}`} data-component="ContentPanel" data-section={section}>
      {/* ── Header ────────────────────────────────────── */}
      <div className="content-panel__header">
        <div className="content-panel__nav">
          {onBack && (
            <button
              className="content-panel__nav-btn"
              onClick={onBack}
              title="Zurück"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
          )}
          {onForward && (
            <button
              className="content-panel__nav-btn"
              onClick={onForward}
              title="Vorwärts"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          )}
        </div>

        {title && (
          <span className="content-panel__header-label">{title}</span>
        )}

        <div className="content-panel__spacer" />

        {headerAction}

        {/* Slot for child components to portal actions into */}
        <div className="content-panel__actions-slot" />

        {onFilter && (
          <button
            className="content-panel__action-btn"
            onClick={onFilter}
            title="Filter"
          >
            filter
          </button>
        )}
        {onCreate && (
          <button
            className="content-panel__action-btn content-panel__action-btn--create"
            onClick={onCreate}
            title="Erstellen"
          >
            +
          </button>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────── */}
      <div className="content-panel__body">
        {children}
      </div>
    </div>
  )
}
